import { createFileRoute } from '@tanstack/react-router'
import { useMemo, type ReactElement } from 'react'
import { z } from 'zod'

import { DataTablePagination } from '~/components/data/DataTablePagination'
import { ScrollableDataRegion } from '~/components/data/ScrollableDataRegion'
import {
  formatEuroCents,
  subscriptionsKeys,
  type AuditEntry,
} from '~/features/subscriptions'
import { http } from '~/lib/http'
import {
  toPaginatedResult,
  usePaginatedQuery,
  type PaginatedParams,
  type PaginatedResult,
} from '~/lib/query/use-paginated-query'
import { useUrlState } from '~/lib/query/use-url-state'

/**
 * `/admin/subscriptions/audit` — Subscription audit log (URL-paginated).
 *
 * URL search keys: `page`, `limit`, `source`.
 *
 * The audit endpoint historically supported cursor pagination; with the
 * envelope migration it now also accepts page/limit. We feed both so
 * either contract returns a usable response.
 */
const auditSearchSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(10).max(100).default(50),
  source: z.enum(['all', 'admin', 'customer', 'system', 'webhook']).default('all'),
})
type AuditSearch = z.infer<typeof auditSearchSchema>

const URL_DEFAULTS: AuditSearch = {
  page: 1,
  limit: 50,
  source: 'all',
}

export const Route = createFileRoute('/admin/subscriptions/audit')({
  validateSearch: auditSearchSchema,
  component: AuditLogPage,
})

interface AuditListPayload {
  readonly entries?: readonly AuditEntry[]
  readonly items?: readonly AuditEntry[]
  readonly total?: number
  readonly pagination?: {
    readonly page?: number
    readonly limit?: number
    readonly total?: number
    readonly totalPages?: number
  }
}

async function fetchAuditPage(
  params: PaginatedParams,
): Promise<PaginatedResult<AuditEntry>> {
  const query: Record<string, string | number | undefined> = {
    page: params.page,
    limit: params.limit,
  }
  if (params.sort) query.sort = params.sort
  if (params.filters?.source && params.filters.source !== 'all') {
    query.source = params.filters.source
  }

  try {
    const raw = await http.get<AuditListPayload>(
      '/api/v1/admin/subscriptions/audit',
      { query },
    )
    const rows = (raw.entries ?? raw.items ?? []) as readonly AuditEntry[]
    const total = raw.pagination?.total ?? raw.total ?? rows.length
    return toPaginatedResult(rows, total, params, raw.pagination)
  } catch {
    return toPaginatedResult<AuditEntry>([], 0, params)
  }
}

function AuditLogPage(): ReactElement {
  const search = Route.useSearch()
  const [, setUrl] = useUrlState(auditSearchSchema, URL_DEFAULTS)

  const params: PaginatedParams = {
    page: search.page,
    limit: search.limit,
    filters: { source: search.source },
  }

  const audit = usePaginatedQuery<AuditEntry>({
    queryKey: [...subscriptionsKeys.all, 'audit-paginated'],
    params,
    fetcher: fetchAuditPage,
  })

  const entries = useMemo(() => audit.data?.data ?? [], [audit.data])
  const pagination = audit.data?.pagination

  return (
    <div className="ka-subs-list-page">
      <header className="ka-page-header">
        <div>
          <h1 className="ka-page-header-title">Audit Log</h1>
          <p className="ka-page-header-subtitle">
            {pagination?.total ?? entries.length} entries
          </p>
        </div>
        <div className="ka-page-header-actions">
          <button type="button" className="ka-btn ka-btn--ghost">
            Export CSV
          </button>
        </div>
      </header>

      <div className="ka-filter-strip" role="search">
        <div className="ka-filter-group" role="group" aria-label="Source">
          <span className="ka-filter-group-label">Source</span>
          {(['all', 'admin', 'customer', 'system', 'webhook'] as const).map(
            (s) => (
              <button
                key={s}
                type="button"
                className={`ka-detail-chip${search.source === s ? ' is-active' : ''}`}
                aria-pressed={search.source === s}
                onClick={() => setUrl({ source: s, page: 1 })}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ),
          )}
        </div>
      </div>

      <div className="ka-card ka-card--flush ka-subs-table-card">
        <ScrollableDataRegion label="Subscription audit log" minWidth="840px">
          <table className="ka-table">
          <thead>
            <tr>
              <th scope="col">Timestamp</th>
              <th scope="col">Actor</th>
              <th scope="col">Verb</th>
              <th scope="col">Target</th>
              <th scope="col" className="ka-num">
                Amount
              </th>
              <th scope="col">Source</th>
            </tr>
          </thead>
          <tbody>
            {audit.isPending ? (
              <tr>
                <td colSpan={6} className="ka-table-empty" aria-busy="true">
                  Loading audit entries…
                </td>
              </tr>
            ) : entries.length === 0 ? (
              <tr>
                <td colSpan={6} className="ka-table-empty">
                  No audit entries match the active filters.
                </td>
              </tr>
            ) : (
              entries.map((e) => (
                <tr key={e.id}>
                  <td className="ka-td-meta">
                    {e.occurred_at.slice(0, 19).replace('T', ' ')}
                  </td>
                  <td className="ka-td-meta">{e.actor_name ?? 'system'}</td>
                  <td>
                    <span className="ka-chip ka-chip--info">{e.verb}</span>
                  </td>
                  <td className="ka-td-meta">
                    {e.target_label ?? '—'}
                    {e.diff_summary ? (
                      <span className="ka-count-chip-sep">
                        {e.diff_summary}
                      </span>
                    ) : null}
                  </td>
                  <td className="ka-num">
                    {e.amount_cents != null
                      ? formatEuroCents(e.amount_cents)
                      : '—'}
                  </td>
                  <td>
                    <span className="ka-chip ka-chip--muted">
                      {e.source ?? 'system'}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
          </table>
        </ScrollableDataRegion>
        <DataTablePagination
          className="ka-subs-list-pagination"
          page={pagination?.page ?? search.page}
          limit={pagination?.limit ?? search.limit}
          total={pagination?.total ?? entries.length}
          isLoading={audit.isPending}
          onChange={({ page, limit }) => setUrl({ page, limit })}
        />
      </div>
    </div>
  )
}
